import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, GitCompareArrows } from "lucide-react";
import Seo, { breadcrumbLd } from "@/components/Seo";
import { MarketingNav, MarketingFooter, MarketingCTA } from "@/components/MarketingChrome";
import { COMPETITORS } from "@/data/seoContent";

export default function CompareIndex() {
  const jsonLd = breadcrumbLd([{ name: "Home", path: "/" }, { name: "Compare", path: "/compare" }]);
  return (
    <div className="font-body bg-sand-50 text-ink min-h-screen">
      <Seo
        title="Ask Redline vs TownSq, Vantaca, CINC, PayHOA & More, Compare HOA Software"
        description="Compare Ask Redline with TownSq, Vantaca, CINC Systems, PayHOA, FrontSteps and AppFolio. See how an AI text & call concierge stacks up for resident communication."
        canonical="/compare"
        jsonLd={jsonLd}
        scrollTop
      />
      <MarketingNav />

      <section className="max-w-6xl mx-auto px-6 pt-14 pb-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-redline-50 text-redline-700 text-[11px] font-bold uppercase tracking-[0.16em] px-3 py-1.5 mb-5">
            <GitCompareArrows className="h-3.5 w-3.5" strokeWidth={2} /> Compare
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.08]">How Ask Redline compares.</h1>
          <p className="mt-5 text-lg text-ink-700 leading-relaxed">
            Most HOA platforms started as a portal or an accounting engine. Ask Redline started as the resident conversation, 
            an AI concierge that answers by text and phone, cited to your governing documents. See how it stacks up, honestly.
          </p>
        </motion.div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-16 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {COMPETITORS.map((c, i) => (
          <motion.div key={c.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}>
            <Link to={`/compare/${c.slug}`} data-testid={`compare-card-${c.slug}`}
              className="group flex flex-col h-full rounded-3xl border border-sand-200 bg-white p-7 hover:border-redline-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_-24px_rgba(15,15,20,0.25)] transition-all">
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink-400">{c.category}</div>
              <div className="font-heading text-2xl font-extrabold tracking-tight mt-2">Ask Redline <span className="text-ink-400">vs</span> {c.competitor}</div>
              <p className="text-sm text-ink-600 mt-3 leading-relaxed flex-1">{c.hero.sub}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-redline-600 group-hover:gap-2.5 transition-all">
                See the comparison <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          </motion.div>
        ))}
      </section>

      <MarketingCTA />
      <MarketingFooter />
    </div>
  );
}
